import { useMutation, useQueryClient, UseQueryOptions } from "react-query"
import { useApiClient } from "../../api-client/api-client.hooks"
import type { TApiGenericError } from "../../timenotes-query/timenotes-query.types"
import type { TApiTask, TApiTaskDetails } from "../../api-client/types"

interface Args {
  taskId: string
  body: Partial<TApiTask>
}

interface useUpdateTaskMutationProps {
  projectId: string
}

export function useUpdateTaskMutation(props: useUpdateTaskMutationProps, options?: UseQueryOptions<any, any>) {
  const {
    projectId,
  } = props

  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  const mutation = useMutation<{}, TApiGenericError<any>, Args>(
    ["tasks-update"],
    async (params: Args) => {
      const response = await apiClient.updateTask(
        projectId,
        params.taskId,
        params.body
      )

      if (!response.ok) {
        throw response.errors
      }

      return response
    },
    {
      ...options as any,
      onMutate: (vars) => {
        // Prevent optimistic load for tags as they are sent by IDs, not Tags
        if (vars.body.tags) return {}

        const prevData = queryClient.getQueryData(['project-task', projectId, vars.taskId])

        queryClient.setQueryData(['project-task', projectId, vars.taskId], (oldData) => {
          const oldTask = oldData.task as TApiTaskDetails

         return {
            task: {
              ...oldTask,
              ...vars.body,
            }
          }
        })

        return {
          prevTask: prevData?.task,
        }
      },
      onError: (error, vars, context) => {
        if (context?.prevTask) {
          const oldTask = context.prevTask as TApiTaskDetails

          queryClient.setQueryData(['project-task', projectId, vars.taskId], (old) => {
            return {
              task: oldTask,
            }
          })
        }
      },
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(["project-sections", projectId], { exact: false })
        queryClient.invalidateQueries(["project-task", projectId, variables.taskId])

        if (options?.onSuccess) options.onSuccess(data, variables)
      },
    }
  )

  return mutation
}
